import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import _ from 'lodash';
import { combineLatest, isObservable, merge, Observable, of, switchMap, tap } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

export interface DropdownOptionItem {
  value: DropdownItemValue;
  label: string;
  sublabel?: string;
}

type DropdownItemValue = string | number | boolean;

type DropdownOptionsFn = (
  searchString: string,
  pageNumber?: number,
  pageSize?: number,
) => Observable<Record<string, DropdownItemValue>[]>;

type DropdownOptions =
  | DropdownOptionsFn
  | Observable<Record<string, DropdownItemValue>[]>
  | Record<string, DropdownItemValue>[];

interface IOptionsArgs {
  pageNumber?: number;
  pageSize?: number;
}

@Component({
  selector: 'app-filter-dropdown',
  templateUrl: './filter-dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterDropdownComponent),
      multi: true,
    },
  ],
  styles: `
    ::ng-deep .filter-search-box {
      position: sticky;
      top: 0;
      z-index: 10;
      background-color: var(--mat-select-panel-background-color);

      .mat-mdc-form-field-subscript-wrapper {
        display: none !important;
      }
    }

    :host {
      ::ng-deep .mat-mdc-form-field-subscript-wrapper {
        display: none !important;
      }
    }
  `,
  imports: [
    TranslateModule,
    MatSelectModule,
    ReactiveFormsModule,
    AsyncPipe,
    MatIcon,
    MatInput,
    MatProgressSpinner,
    NgClass,
  ],
})
export class FilterDropdownComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() public label!: string;
  @Input() public multiple = false;
  @Input() public showSearch = true;
  @Input() public allowNew = false;
  @Input() public filterPlaceholder = 'global.filter.searchPlaceholder';
  @Input() public options!: DropdownOptions;
  @Input() public dynamicLookup = false;
  @Input() public optionsValuePath = '';
  @Input() public optionsArgs: IOptionsArgs = { pageNumber: 1, pageSize: 50 };
  @Input() public valuePath = 'value';
  @Input() public labelPath = 'label';

  protected readonly destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  private optionsCache: DropdownOptionItem[] = [];
  private valueCache: DropdownItemValue | DropdownItemValue[] | null = null;
  public isLoading = true;
  public value: DropdownItemValue | DropdownItemValue[] | null = null;
  public filterControl = new FormControl();
  public selectionControl = new FormControl<DropdownOptionItem | null>(null);
  public selectionControlMulti = new FormControl<DropdownOptionItem[]>([]);
  public filteredOptions$ = new Observable<DropdownOptionItem[]>();

  public showClearButton$ = merge(
    of(null), //initial check,
    this.selectionControl.valueChanges,
    this.selectionControlMulti.valueChanges,
  ).pipe(
    // Map to boolean based on current form control values
    map(() => {
      if (this.multiple) {
        return this.multiple && Array.isArray(this.selectionControlMulti?.value)
          ? this.selectionControlMulti?.value.length > 0
          : false;
      }
      return Array.isArray(this.selectionControl.value)
        ? this.selectionControl.value.length > 0
        : Boolean(this.selectionControl.value);
    }),
    shareReplay(1),
  );

  public ngOnChanges(changes: SimpleChanges): void {
    const optionsInput: DropdownOptions = changes['options']?.currentValue;

    if (optionsInput) {
      const optionsObsFn = this.resolveInputObservable(optionsInput);

      this.filteredOptions$ = this.dynamicLookup
        ? this.handleDynamicOptions(optionsObsFn)
        : this.handleStaticOptions(optionsObsFn);
    }
  }

  public ngOnDestroy(): void {
    this.selectionControl.reset();
    this.selectionControlMulti.reset();
    this.filterControl.reset();
    this.valueCache = null;
  }

  private resolveInputObservable(optionsInput: DropdownOptions): DropdownOptionsFn {
    if (isObservable(optionsInput)) {
      return () => optionsInput;
    } else if (typeof optionsInput === 'object') {
      return () => of(optionsInput as Record<string, DropdownItemValue>[]);
    } else {
      return optionsInput as DropdownOptionsFn;
    }
  }

  private handleDynamicOptions(optionsObsFn: DropdownOptionsFn): Observable<DropdownOptionItem[]> {
    if (this.valueCache) {
      this.filterControl.setValue(this.valueCache);
    }
    return this.filterControl.valueChanges.pipe(
      startWith(this.filterControl.value),
      switchMap((filterValue) => optionsObsFn(filterValue, this.optionsArgs.pageNumber, this.optionsArgs.pageSize)),
      map((options) => {
        this.optionsCache = this.mapOptions(_.get(options, this.optionsValuePath, options));
        return this.optionsCache;
      }),
      tap(() => this.finishLoading()),
    );
  }

  private handleStaticOptions(optionsObsFn: DropdownOptionsFn): Observable<DropdownOptionItem[]> {
    const options$ = optionsObsFn('').pipe(
      map((options) => this.setOptionsCache(options)),
      shareReplay(1),
    );

    const filteredOptions$ = this.filterControl.valueChanges.pipe(
      startWith(this.filterControl.value || ''),
      map((filterValue) => (filterValue || '').toLowerCase()),
    );

    return combineLatest([options$, filteredOptions$]).pipe(
      map(([mappedOptions, filterValue]) =>
        mappedOptions.filter((option) => option.label.toLowerCase().includes(filterValue)),
      ),
      tap(() => this.finishLoading()),
    );
  }

  private finishLoading(): void {
    this.isLoading = false;
    if (this.valueCache) {
      this.writeValue(this.valueCache);
    }
    this.cdr.detectChanges();
  }

  private setOptionsCache(options: Record<string, DropdownItemValue>[]): DropdownOptionItem[] {
    const mappedOptions = this.mapOptions(_.get(options, this.optionsValuePath, options));
    this.optionsCache = mappedOptions;
    return mappedOptions;
  }

  private mapOptions(options: Record<string, DropdownItemValue>[]): DropdownOptionItem[] {
    return options.map((option) => {
      if (typeof option === 'object') {
        return {
          ...option,
          value: _.get(option, this.valuePath),
          label: _.get(option, this.labelPath) as string,
        };
      } else {
        return {
          value: option,
          label: option as string,
        };
      }
    });
  }

  public onSelectionChange(event: MatSelectChange): void {
    this.updateSelected(event.value);
  }

  public onClearInput(event: MouseEvent): void {
    event.stopPropagation();
    this.clearInput();
  }

  private clearInput(): void {
    this.valueCache = null;
    this.writeValue(null);
    this.updateSelected(null);
    this.filterControl.reset();
    this.selectionControl.reset();
    this.selectionControlMulti.reset();
  }

  public keyDownHandler(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.stopPropagation();
    }
  }

  public updateSelected(value: DropdownOptionItem | DropdownOptionItem[] | null): void {
    if (value) {
      this.value = Array.isArray(value) ? value?.map((x) => x.value) : value.value;
    } else {
      this.value = null;
    }
    this.onChange(this.value);
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectionControl.disable({ emitEvent: false });
    } else {
      this.selectionControl.enable({ emitEvent: false });
    }
  }

  public setCustomOption(option: DropdownItemValue): void {
    this.value = this.multiple ? [option] : option;
    this.onChange(this.value);
  }

  // ControlValueAccessor
  public writeValue(value: DropdownItemValue | DropdownItemValue[] | null): void {
    if (this.value === null) {
      this.valueCache = value;
    }
    this.value = value;

    if (this.multiple) {
      const selectedOption = this.optionsCache.filter((option) =>
        Array.isArray(this.value)
          ? this.value
              .map(String)
              .map((v) => v.trim())
              .includes(String(option.value).trim())
          : String(option.value).trim() === String(this.value).trim(),
      );

      this.selectionControlMulti.setValue(selectedOption ? selectedOption : null);
    } else {
      const selectedOption = this.optionsCache.find((x) => x.value === value) ?? null;
      this.selectionControl.setValue(selectedOption ? selectedOption : null);
    }
  }

  public registerOnChange(onChange: (value: DropdownItemValue | DropdownItemValue[] | null) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public onChange = (value: DropdownItemValue | DropdownItemValue[] | null): void => {
    //placeholder implementation
    console.log(`Value changed ${value}`);
  };

  public onTouched = (): void => {
    //placeholder implementation
  };
}
